import React, { useCallback, useMemo, useState } from "react";
import { ActionProps, ActionState as State } from "./interfaces";
import { useTranslation } from "react-i18next";
import AddNewDocument from "../../../AddNewDocument/AddNewDocument";
import { DocumentDTO } from "../../../../models/documents";
import { DOCUMENT_CATEGORIES, DOCUMENT_TYPES, OUTGOING_MAIL_STATUSES } from "../../../../utils/constants/constants";
import { DocumentCategoryRef } from "../../../../models/document-categories";
import { DocumentTypeRef } from "../../../../models/document-types";
import { Period, PeriodType } from "../../../../models/documents/period";
import { UploadOutgoingMailSendingDocumentApiService } from "../../../../services/upload-outgoing-mail-sending-document/upload-outgoing-mail-sending-document-api.service";
import { UploadOutgoingMailDocumentFilter } from "../../../../models/upload-outgoing-mail-document/upload-outgoing-mail-document-filter";
import { Button, Dialog, DialogContent, Typography, useTheme } from "@mui/material";
import DialogTitle from "src/containers/DialogTitle/DialogTitle";
import LoadingOverlay from "src/containers/LoadingOverlay/LoadingOverlay";
import ConfirmationChangesDialog from "src/containers/ConfirmationChangesDialog/ConfirmationChangesDialog";

const UploadSendingDocument = ({ outgoingMail, handleNotifyParent }: ActionProps) => {
  const { t } = useTranslation();
  const tRP = "components.upload_sending_document";
  const theme = useTheme();

  const INITIAL_STATE: State = useMemo(
    () => ({
      open: false,
      loading: false,
      CCDOpen: false,
      error: "",
    }),
    []
  );

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleLinkDocumentToOutgoingMail = useCallback(
    async (document: DocumentDTO | null) => {
      if (document) {
        setState((state) => ({ ...state, loading: true }));
        try {
          const response = await new UploadOutgoingMailSendingDocumentApiService().loadItems({
            documentId: document.id,
            outgoingMailId: outgoingMail.id,
          } as UploadOutgoingMailDocumentFilter);
          if (response.error) {
            setState((state) => ({
              ...state,
              error: response.error,
            }));
          } else {
            handleNotifyParent();
            setState({ ...INITIAL_STATE });
          }
        } catch (error) {
          setState((state) => ({
            ...state,
            error: error as string,
          }));
        } finally {
          setState((state) => ({ ...state, loading: false }));
        }
      }
    },
    [outgoingMail.id, handleNotifyParent, INITIAL_STATE]
  );

  return (
    <>
      {outgoingMail.status?.id === OUTGOING_MAIL_STATUSES.Discarded.id ? (
        <Typography
          style={{ color: theme.palette.secondary.light }}
          title={`${t(`${tRP}.already_discarded`)} ${t(`${tRP}.not_allowed`)}`}
        >{`${t(`${tRP}.title`)}`}</Typography>
      ) : (
        <Button
          onClick={() => setState((state) => ({ ...state, open: true }))}
          style={{ color: theme.palette.text.primary }}
        >
          {outgoingMail.sendingDocument ? `${t(`${tRP}.re_title`)}` : `${t(`${tRP}.title`)}`}
        </Button>
      )}
      <Dialog
        open={state.open}
        fullWidth
      >
        <DialogTitle
          title={`${t(`${tRP}.title`)}`}
          handleClose={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
        <DialogContent>
          <LoadingOverlay
            loading={state.loading}
            error={state.error}
          >
            <AddNewDocument
              buttonType="text"
              openButtonKey={`${t(`${tRP}.title`)}`}
              handleNotifyParent={handleLinkDocumentToOutgoingMail}
              displayExpandableItemUponSave={false}
              maxFilesNumber={1}
              isGenericDocument
              documentFieldsProps={{
                category: DOCUMENT_CATEGORIES.OTHER as DocumentCategoryRef,
                type: DOCUMENT_TYPES.Other as DocumentTypeRef,
                period: Period.makePeriod(new Date().getFullYear(), PeriodType.Month, new Date().getMonth() + 1),
                documentDate: new Date(),
              }}
              readOnlyFields={{
                isPeriodRO: true,
                isLegalEntityRO: true,
                isTypeRO: true,
                isCategoryRO: true,
                isDateRO: true,
              }}
              hideReadOnlyFields
              wrappedInDialog={false}
            />
          </LoadingOverlay>
        </DialogContent>
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() => setState({ ...INITIAL_STATE })}
      />
    </>
  );
};

export default UploadSendingDocument;
